import { TICK_DT } from './constants.ts';
import { dist, type Vec3 } from './math.ts';
import { isEmbodied, type PlayerState } from './player.ts';
import { eyePos, type SimState, type WorldQuery } from './sim.ts';

/**
 * What a single player is entitled to know about the world.
 *
 * This is the one security measure that actually matters in a browser game.
 * Everything else about cheating is either already solved — the server owns the
 * simulation, so a client cannot claim to have dealt damage — or unsolvable,
 * since a client can read its own memory and no amount of obfuscation changes
 * that. What *is* solvable is the middle case: a modified client can only draw
 * an enemy through a wall if the server told it that enemy was there.
 *
 * So the rule is that a player is sent what they could plausibly perceive, and
 * nothing else. It is not obfuscation — the data genuinely never leaves the
 * server — which is why it holds against a client rewritten from scratch.
 *
 * It pays twice. The same filter that stops a wallhack cuts the size of every
 * update, and bandwidth is the thing that decides how many players a room can
 * hold long before processor time does.
 *
 * Kept in core, and pure. It runs on the server against real state, and it can
 * run in the local transport too — which is how it gets tested and played
 * against long before there is a network to test it over.
 */

/**
 * How long an enemy stays in the picture after slipping out of sight.
 *
 * Without a grace period an enemy crossing a pillar strobes in and out several
 * times a second, which is worse to look at than a wallhack and unmistakably a
 * bug. With one, they keep moving on the last thing you actually saw — see
 * `Sighting`, which is the part that stops the grace period leaking live
 * positions.
 */
const MEMORY_SECONDS = 1.5;

/** Past this, an enemy is out of the picture whatever the line of sight says. */
const SIGHT_RANGE = 70;

/**
 * How far a mage is noticed without being seen.
 *
 * Somebody casting at your back should not be invisible to you because a pillar
 * is in the way — you would hear it. Generous, because being killed by something
 * that was never rendered is the single most infuriating thing a game can do,
 * and a modest radius of honest information is cheaper than that feeling.
 */
const EARSHOT = 12;

/** How near a projectile must be to be worth telling somebody about. */
const PROJECTILE_RANGE = 45;

/** And a placed effect, which is a trap and should not be given away. */
const PLACED_RANGE = 22;

/**
 * The last thing a viewer actually saw of somebody.
 *
 * The reason a grace period does not simply leak positions. When an enemy goes
 * out of sight they do not vanish and they do not keep updating — they keep
 * being *where they were*, which is exactly what a player's own memory would
 * hold. A client that draws them at that position is drawing something honest
 * and out of date, which is the correct thing to be wrong about.
 */
interface Sighting {
  at: number;
  pos: Vec3;
  yaw: number;
  hp: number;
}

/** One viewer's picture of the world, carried between updates. */
export interface Awareness {
  readonly viewerId: string;
  readonly seen: Map<string, Sighting>;
}

export function makeAwareness(viewerId: string): Awareness {
  return { viewerId, seen: new Map() };
}

/**
 * Whether a viewer can see a given mage right now.
 *
 * Eye to eye, because a mage crouched behind a low wall with only their head
 * showing is visible, and a mage whose feet are visible under a gap is not
 * something this game's geometry can produce.
 */
/**
 * How far ahead visibility looks, seconds. A client draws its own mage a round
 * trip ahead of the room, so by the time the room decides an enemy has come
 * into view, the player has already been looking at that corner for a round
 * trip. Testing where both of them are about to be closes that gap: an enemy is
 * sent a moment *before* the eye line clears, and is already moving smoothly
 * when it does, instead of materialising in a new place.
 */
const SIGHT_LEAD = 0.15;
/** Furthest a lead may move a test point, so a teleport is not extrapolated. */
const SIGHT_LEAD_MAX = 1.5;
/**
 * How far either side of the eye line the test reaches, metres. A shoulder
 * round a corner is a mage in sight, and so is a peek: the eye-to-eye ray alone
 * said "hidden" until the moment the two faces lined up.
 */
const SIGHT_WIDTH = 0.45;

/**
 * After losing sight, how long an enemy is still sent *live* rather than frozen
 * where they were last seen. A third of a second is a quick step back behind
 * cover and out again — the peek the player is actually doing — and too short
 * to be worth anything as a wallhack. Past it, the memory is a still image, as
 * before.
 */
const LIVE_GRACE = 0.3;

/** A body's movement over the last tick, carried `SIGHT_LEAD` forward and capped. */
function lead(p: PlayerState): Vec3 {
  const was = p.prevPos ?? p.pos;
  const dx = (p.pos.x - was.x) / TICK_DT * SIGHT_LEAD;
  const dy = (p.pos.y - was.y) / TICK_DT * SIGHT_LEAD;
  const dz = (p.pos.z - was.z) / TICK_DT * SIGHT_LEAD;
  const len = Math.hypot(dx, dy, dz);
  if (len < 1e-6) return { x: 0, y: 0, z: 0 };
  const k = len > SIGHT_LEAD_MAX ? SIGHT_LEAD_MAX / len : 1;
  return { x: dx * k, y: dy * k, z: dz * k };
}

/**
 * Whether any part of `other` could be in view of `viewer`, now or a moment
 * from now: from the viewer's eye and either side of it, to the other's head,
 * chest, knees and both shoulders — and the same again with both carried along
 * their current movement. The first clear line answers; most pairs are decided
 * by the first ray or by range.
 */
function canSee(viewer: PlayerState, other: PlayerState, world: WorldQuery): boolean {
  if (dist(viewer.pos, other.pos) > SIGHT_RANGE) return false;

  const eye = eyePos(viewer);
  const head = eyePos(other);
  // Sideways, across the line between them.
  const fx = head.x - eye.x;
  const fz = head.z - eye.z;
  const flat = Math.hypot(fx, fz) || 1;
  const sx = (-fz / flat) * SIGHT_WIDTH;
  const sz = (fx / flat) * SIGHT_WIDTH;

  const leadV = lead(viewer);
  const leadO = lead(other);
  const shift = (p: Vec3, d: Vec3): Vec3 => ({ x: p.x + d.x, y: p.y + d.y, z: p.z + d.z });

  const feet = other.pos;
  const eyeL = { x: eye.x + sx, y: eye.y, z: eye.z + sz };
  const eyeR = { x: eye.x - sx, y: eye.y, z: eye.z - sz };
  const chest = { x: feet.x, y: feet.y + 1.0, z: feet.z };
  const shL = { x: feet.x + sx, y: feet.y + 1.3, z: feet.z + sz };
  const shR = { x: feet.x - sx, y: feet.y + 1.3, z: feet.z - sz };

  /*
   * Pairs, not every combination: each side of the viewer's head is matched
   * with the same side of the target, which is the line a peek actually opens.
   * Eight rays for an enemy who is properly hidden, four more if either of them
   * is moving — and most pairs are settled by the first ray or by range.
   */
  const now: [Vec3, Vec3][] = [
    [eye, head], [eye, chest], [eye, shL], [eye, shR],
    [eyeL, shL], [eyeR, shR], [eyeL, head], [eyeR, head],
  ];
  for (const [a, b] of now) if (!world.segmentBlocked(a, b)) return true;

  if (leadV.x || leadV.y || leadV.z || leadO.x || leadO.y || leadO.z) {
    const soon: [Vec3, Vec3][] = [[eye, head], [eye, chest], [eyeL, shL], [eyeR, shR]];
    for (const [a, b] of soon) if (!world.segmentBlocked(shift(a, leadV), shift(b, leadO))) return true;
  }
  return false;
}

/**
 * Brings a viewer's picture up to date. Call before `viewFor`.
 *
 * Separated from the filtering because the two run at different rates: what a
 * player knows changes as fast as they can turn their head, and what they are
 * *sent* is capped by the tick. Keeping them apart means the expensive half —
 * one line-of-sight query per enemy — can be throttled on its own.
 */
export function observe(
  state: SimState, know: Awareness, world: WorldQuery, now: number,
): void {
  const viewer = state.players.find((p) => p.id === know.viewerId);
  if (!viewer) return;

  for (const other of state.players) {
    if (other.id === viewer.id || other.faction === viewer.faction) continue;
    if (!isEmbodied(other)) continue;

    const near = dist(viewer.pos, other.pos);
    const noticed = near <= EARSHOT || canSee(viewer, other, world);
    if (!noticed) continue;

    know.seen.set(other.id, {
      at: now,
      pos: { ...other.pos },
      yaw: other.yaw,
      hp: other.hp,
    });
  }
}

/**
 * The world as this viewer is entitled to see it.
 *
 * Returns a state of the same shape, so everything downstream — the renderer,
 * the HUD, the damage numbers — works against it unchanged. That is deliberate:
 * a filter that needs the rest of the game to know about it would be a filter
 * nobody could turn on.
 */
export function viewFor(state: SimState, know: Awareness, now: number): SimState {
  const viewer = state.players.find((p) => p.id === know.viewerId);
  if (!viewer) return state;

  const players: PlayerState[] = [];
  const visible = new Set<string>();

  for (const other of state.players) {
    /*
     * Yourself and your own side, always.
     *
     * Not a concession — a Cleric has to be able to find a ghost through a wall
     * to call it, the roster shows every teammate's health, and none of that is
     * an exploit because it is your own team. The whole of the risk is on the
     * other side of the board.
     */
    if (other.id === viewer.id || other.faction === viewer.faction) {
      players.push(other);
      visible.add(other.id);
      continue;
    }

    const sighting = know.seen.get(other.id);
    if (!sighting || now - sighting.at > MEMORY_SECONDS) continue;

    // Seen within the last moment (`LIVE_GRACE`): the real thing. Longer ago:
    // what was seen, where it was seen.
    if (now - sighting.at <= LIVE_GRACE) {
      players.push(other);
    } else {
      players.push({ ...other, pos: { ...sighting.pos }, yaw: sighting.yaw, hp: sighting.hp });
    }
    visible.add(other.id);
  }

  return {
    ...state,
    players,

    /*
     * A spell in the air near you, or one thrown by somebody you can see.
     *
     * Both halves are needed. Distance alone hides a Lightning Bolt fired from
     * across the nave until it is nearly on you; visibility alone hides a Fire
     * Ball lobbed over a wall, which is a thing the spell is *for*.
     */
    projectiles: state.projectiles.filter(
      (pr) => visible.has(pr.ownerId) || dist(pr.pos, viewer.pos) <= PROJECTILE_RANGE,
    ),

    /*
     * Enemy traps are not given away, which is the point of a trap.
     *
     * The Arcanist's Signs have never worked as designed: every client was told
     * where all of them were, so the only thing hiding one was a player not
     * looking at their screen. Their own side still sees them all — laying a
     * trap your team walks into is not interesting either.
     */
    signs: state.signs.filter(
      (sign) => sign.faction === viewer.faction
        || dist(sign.pos, viewer.pos) <= PLACED_RANGE,
    ),

    // Storms and barriers are large, loud and lit. Hiding one would only make
    // a player walk into something a person standing there would have seen.
    storms: state.storms,
    barriers: state.barriers,
  };
}

/**
 * Roughly how much of the roster a viewer is being told about.
 *
 * Exists to be asserted against. A filter that quietly stops filtering is worse
 * than none, because everything still looks correct — so the number it produces
 * has to be something a test can hold on to.
 */
export function relevanceRatio(state: SimState, view: SimState): number {
  if (!state.players.length) return 1;
  return view.players.length / state.players.length;
}
